import { api } from "./client";
import { unwrapApiData } from "./unwrap";
import type { ApiErrorBody } from "./types";

export interface AiSession {
  sessionId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface AiMessage {
  messageId: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

export interface AiChatResponse {
  sessionId: string;
  reply: string;
}

export async function sendAiMessage(
  message: string,
  sessionId?: string,
  model?: string,
): Promise<AiChatResponse> {
  const data = await api.post<{ ok: true; data: AiChatResponse } | ApiErrorBody>(
    "/v1/ai/chat",
    { message, sessionId, model },
  );
  return unwrapApiData(data);
}

export async function listAiSessions(): Promise<AiSession[]> {
  const data = await api.get<{ ok: true; data: AiSession[] } | ApiErrorBody>(
    "/v1/ai/sessions",
  );
  return unwrapApiData(data);
}

export async function loadAiSession(
  sessionId: string,
): Promise<{ session: AiSession; messages: AiMessage[] }> {
  const data = await api.get<
    { ok: true; data: { session: AiSession; messages: AiMessage[] } } | ApiErrorBody
  >(`/v1/ai/sessions/${sessionId}/messages`);
  return unwrapApiData(data);
}

export async function deleteAiSession(sessionId: string): Promise<void> {
  await api.delete<unknown>(`/v1/ai/sessions/${sessionId}`);
}

export async function analyzeReport(
  reportType: string,
  reportData: unknown,
  question?: string,
): Promise<string> {
  const data = await api.post<{ ok: true; data: { analysis: string } } | ApiErrorBody>(
    "/v1/ai/analyze-report",
    { reportType, reportData, question },
  );
  return unwrapApiData(data).analysis;
}

export async function getKnowledgeNotes(): Promise<string> {
  const data = await api.get<{ ok: true; data: { notes: string } } | ApiErrorBody>(
    "/v1/ai/knowledge",
  );
  return unwrapApiData(data).notes;
}

export async function saveKnowledgeNotes(notes: string): Promise<void> {
  await api.put<unknown>("/v1/ai/knowledge", { notes });
}

export interface ChartConfig {
  type: "bar" | "line" | "pie" | "table";
  title?: string;
  xKey?: string;
  yKey?: string;
}

export interface DbChatResult {
  narrative: string;
  sql: string;
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount?: number;
  executionMs?: number;
  chart?: ChartConfig;
  llmProvider?: "openrouter" | "workers_ai";
  llmModel?: string;
}

export interface SavedDbQuery {
  queryId: string;
  label: string;
  question: string;
  sortOrder: number;
  createdAt: string;
  builtin?: boolean;
}

export async function listDbSavedQueries(): Promise<SavedDbQuery[]> {
  const data = await api.get<{ ok: true; data: SavedDbQuery[] } | ApiErrorBody>(
    "/v1/ai/db-analyst/saved",
  );
  return unwrapApiData(data);
}

export async function saveDbQuery(label: string, question: string): Promise<SavedDbQuery> {
  const data = await api.post<{ ok: true; data: SavedDbQuery } | ApiErrorBody>(
    "/v1/ai/db-analyst/saved",
    { label, question },
  );
  return unwrapApiData(data);
}

export async function deleteDbSavedQuery(queryId: string): Promise<void> {
  await api.delete<unknown>(`/v1/ai/db-analyst/saved/${encodeURIComponent(queryId)}`);
}

export async function askFinancialDb(question: string): Promise<DbChatResult> {
  return askDbAnalyst(question);
}

/** Text-to-SQL analyst (schema-only to LLM, execute on D1). */
export async function askDbAnalyst(question: string): Promise<DbChatResult> {
  const data = await api.post<{ ok: true; data: DbChatResult } | ApiErrorBody>(
    "/v1/ai/db-analyst",
    { question },
  );
  return unwrapApiData(data);
}

export interface InspectorFinding {
  severity: "info" | "warning" | "critical";
  title: string;
  detail: string;
}

export interface InspectorResult {
  summary: string;
  findings: InspectorFinding[];
  tailwindFixes?: string[];
  reactHints?: string[];
  idePrompt?: string;
  accountingNotes?: string[];
}

export async function runAiInspector(payload: {
  question: string;
  mode?: "ui" | "accounting" | "ide" | "general";
  route?: string;
  componentHint?: string;
  contextLabel?: string;
  contextJson?: unknown;
}): Promise<InspectorResult> {
  const data = await api.post<{ ok: true; data: InspectorResult } | ApiErrorBody>(
    "/v1/ai/inspector",
    payload,
  );
  return unwrapApiData(data);
}
